import { Component } from 'react';
import {
    differenceBy,
    some,
    filter,
    isEqual,
    get,
    head,
    isEmpty
} from 'lodash';

import AutocompleteDataSource from 'common/components/AutocompleteDataSource/AutocompleteDataSource';
import DataTypeEditor from 'common/components/DataTypeEditor/DataTypeEditor';
import ValueTags from 'common/components/ValueTags/ValueTags';
import styles from '../ValueSets.scss';

class MultiselectList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editorValue: null,
            hasError: false,
            errorMessage: null
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(prevProps.validationErrors, this.props.validationErrors)) {
            this.setState({
                errorMessage: get(head(this.props.validationErrors), 'message')
            });
        }
    }

    onEditorValueChange(value) {
        this.setState({
            editorValue: value
        });
    }

    isSelectedValueValid() {
        const {
            hasError,
            editorValue,
        } = this.state;
        const { value: selectedValues } = this.props;

        const isValueSelected = some(selectedValues, selectedValue => selectedValue === editorValue);

        return !hasError && Boolean(editorValue) && !isValueSelected;
    }

    onValueSelect() {
        const { editorValue } = this.state;

        const isValid = this.isSelectedValueValid();
        if (!isValid) {
            return this.setState({
                errorMessage: `${editorValue} is not an allowed value`
            });
        }

        const {
            changeHandler,
            value: selectedValues=[]
        } = this.props;

        const newValues = [
            ...selectedValues,
            editorValue
        ];

        this.setState({
            editorValue: '',
            hasError: false
        });
        changeHandler(newValues);
    }

    onValueRemove(valueToRemove) {
        const {
            changeHandler,
            value: selectedValues
        } = this.props;

        const newValues = filter(selectedValues, selectedValue => selectedValue !== valueToRemove);
        changeHandler(newValues);
    }

    onKeyPress(evt) {
        if (evt.key === 'Enter') {
            evt.stopPropagation();
            this.onValueSelect();
        }
    }

    onValidStateChange(autocompleteSource, { hasError, editorValue }) {
        this.setState({
            hasError,
            errorMessage: null
        });
    }

    formatAvailableValues() {
        const {
            value: selectedValues,
            availableValues,
            dataType
        } = this.props;

        if (some(availableValues, availableValue => typeof availableValue === 'object')) {
            return filter(availableValues, availableValue => !selectedValues.includes(availableValue.value));
        }

        return differenceBy(availableValues, selectedValues)
            .map((value, index) => {
                return {
                    value: value,
                    text: value,
                    dataType: dataType,
                    order: index
                };
            });
    }

    shouldShowError() {
        const { hasError } = this.state;
        const {
            value: selectedValues,
            validationErrors
        } = this.props;

        return hasError || (isEmpty(selectedValues) && !isEmpty(validationErrors));
    }

    render() {
        const {
            value: selectedValues=[],
            dataType
        } = this.props;
        const { errorMessage } = this.state;

        const values = this.formatAvailableValues();

        return (
            <div onKeyPress={this.onKeyPress.bind(this)}
                className={styles.container} data-cy="multiselectList">
                <ValueTags
                    values={selectedValues}
                    onRemove={this.onValueRemove.bind(this)} />
                { this.shouldShowError() &&
                    <span className={styles.errorMessage}>
                        {errorMessage}
                    </span>
                }
                <DataEntry
                    onChange={this.onEditorValueChange.bind(this)}
                    onBlur={this.onValueSelect.bind(this)}
                    dataType={dataType}
                    hasError={this.shouldShowError()}
                    availableValues={values}
                    onValidStateChange={this.onValidStateChange.bind(this)}
                    value={this.state.editorValue}
                    showDisplayText
                />
            </div>
        );
    }
}

function DataEntry({
    availableValues,
    value,
    onChange,
    onBlur,
    dataType,
    onValidStateChange,
    hasError,
    showDisplayText
}) {
    return (
        <AutocompleteDataSource
            values={availableValues}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            dataType={dataType}
            validate={true}
            onValidStateChange={onValidStateChange}
            showDisplayText={showDisplayText}
        >
            <DataTypeEditor
                className={`${styles.dataTypeEditor} ${hasError && styles.dataTypeEditorError}`} />
        </AutocompleteDataSource>
    );
}

export default MultiselectList;
